/**
 * Manual pricebook attribution — fixing which ServiceTitan pricebook item an
 * invoice or receipt line is matched to, when the automatic match (SKU, vendor
 * part number, then a semantic search) picked the wrong one or none. A button
 * owning the dialog and the mutation, like `manual-job-match.tsx`.
 *
 * The server keeps a manual choice through later re-matches and moves (or
 * retires) the line's pending cost review to agree with it.
 */
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { useMatchLinePricebookItem } from "@/accounts-payable/query";
import type { ApDocumentType, ApInvoiceItem } from "@/accounts-payable/types";
import { hasPricebookMatch } from "@/accounts-payable/utils";
import { PricebookMatchDialog } from "./pricebook-match-dialog";
import { PricebookMatchForm } from "./pricebook-match-form";

export const PricebookMatchButton = ({
  businessId,
  documentId,
  documentType,
  agentRunId,
  item,
}: {
  businessId: string | null;
  documentId: string;
  documentType: ApDocumentType;
  /** The document's newest run id, to tell whether the pick queued a new one. */
  agentRunId: string | null;
  item: ApInvoiceItem;
}) => {
  const [open, setOpen] = useState(false);
  const action = hasPricebookMatch(item) ? "Change" : "Match";
  // Here rather than in the form: the form unmounts with the dialog, and
  // React Query only runs mutate-level callbacks while the observer lives.
  const matchLine = useMatchLinePricebookItem(businessId, documentType);

  return (
    <>
      <Button
        variant="link"
        className="h-auto p-0 text-xs"
        onClick={() => setOpen(true)}
        // Starts with the visible word, so voice control finds it by name.
        aria-label={`${action} pricebook item for ${item.description}`}
      >
        {action}
      </Button>
      <PricebookMatchDialog
        open={open}
        onOpenChange={setOpen}
        saving={matchLine.isPending}
      >
        <PricebookMatchForm
          businessId={businessId}
          documentId={documentId}
          documentType={documentType}
          agentRunId={agentRunId}
          item={item}
          matchLine={matchLine}
          onClose={() => setOpen(false)}
        />
      </PricebookMatchDialog>
    </>
  );
};
